import type { PipelineStage } from '../../../../network/ats';

/**
 * Candidate email templates. {{candidateName}}, {{jobTitle}} and
 * {{companyName}} are filled in when the email is sent; text in [square
 * brackets] is for the recruiter to replace before sending.
 */
export type MessageTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
  /** The stage the candidate moves to when this is sent. */
  stage?: PipelineStage;
};

export const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    id: 'interview-invite',
    name: 'Interview invitation',
    stage: 'interview',
    subject: 'Interview invitation: {{jobTitle}} at {{companyName}}',
    body: `Dear {{candidateName}},

Thank you for applying for the {{jobTitle}} role at {{companyName}}. We enjoyed reading your application and would like to invite you to an interview.

The interview will last about [45 minutes] and will be held [by video call / at our office, address]. Please reply with your availability for [two or three time slots] over the coming week, and we will confirm a time.

If you have any questions in the meantime, just reply to this email.

Kind regards,
[Your name]
{{companyName}}`,
  },
  {
    id: 'offer',
    name: 'Job offer',
    stage: 'offer',
    subject: 'Your offer for {{jobTitle}} at {{companyName}}',
    body: `Dear {{candidateName}},

We are delighted to offer you the position of {{jobTitle}} at {{companyName}}.

Summary of the offer:
- Start date: [date]
- Salary: [amount and currency, per year/month]
- Working pattern: [full-time / part-time, on-site / hybrid / remote]
- Reporting to: [manager name and title]

Your formal offer letter and contract will follow separately. Please let us know by [date] whether you accept, and do get in touch if you would like to discuss any part of the offer.

We look forward to welcoming you to the team.

Kind regards,
[Your name]
{{companyName}}`,
  },
  {
    id: 'rejection',
    name: 'Not moving forward',
    stage: 'rejected',
    subject: 'Your application for {{jobTitle}} at {{companyName}}',
    body: `Dear {{candidateName}},

Thank you for your interest in the {{jobTitle}} role at {{companyName}} and for the time you spent on your application.

After careful consideration, we have decided not to move forward with your application on this occasion. This was not an easy decision, and we appreciate the effort you put in.

We will keep your details on file and may contact you if a suitable role opens up. We wish you every success in your search.

Kind regards,
[Your name]
{{companyName}}`,
  },
];

/** Fills the placeholders the browser already knows, for the preview. */
export function previewTemplate(
  text: string,
  values: { candidateName?: string; jobTitle?: string },
): string {
  return text
    .replace(/\{\{\s*candidateName\s*\}\}/g, values.candidateName || '{{candidateName}}')
    .replace(/\{\{\s*jobTitle\s*\}\}/g, values.jobTitle || '{{jobTitle}}');
}
